import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import type { WorkflowStepInstanceOutput } from "@/types";
import { log } from "@/helpers";
import { getOptionConstraints } from "./utils";

export interface SingleOutputState {
  outputId: string;
  isApproved: boolean;
  hasFeedback: boolean;
  submittedFeedback: boolean; // Tracks if feedback was submitted this session
  pendingFeedback: string | null; // Unsaved feedback text
  selectedOptions: string[] | null; // For Options type outputs
  selectedCount: number; // Track number of selected options for constraint validation
  isFeedbackPanelOpen: boolean; // Tracks if feedback panel is currently open
}

export interface OutputStateByStep {
  byOutputId: Record<string, SingleOutputState>;
}

export interface OutputStateSliceState {
  byStepInstance: Record<string, OutputStateByStep>; // key is workflowStepInstanceId
}

const initialState: OutputStateSliceState = {
  byStepInstance: {},
};

export const outputStateSlice = createSlice({
  name: "outputState",
  initialState,
  reducers: {
    // Initialise output state for a step
    initialiseStepOutputState: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputs: WorkflowStepInstanceOutput[];
      }>,
    ) => {
      const { stepInstanceId, outputs } = action.payload;

      if (!state.byStepInstance[stepInstanceId]) {
        state.byStepInstance[stepInstanceId] = { byOutputId: {} };
      }

      const stepState = state.byStepInstance[stepInstanceId];
      outputs.forEach((output) => {
        if (!stepState.byOutputId[output.id]) {
          stepState.byOutputId[output.id] = {
            outputId: output.id,
            isApproved: output.isApproved,
            hasFeedback: output.hasFeedback,
            submittedFeedback: output.hasFeedback,
            pendingFeedback: null,
            selectedOptions: null,
            selectedCount: 0,
            isFeedbackPanelOpen: false,
          };
        }
      });
    },

    setOutputApproval: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        isApproved: boolean;
      }>,
    ) => {
      const { stepInstanceId, outputId, isApproved } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[outputId].isApproved =
          isApproved;

        // When approving, clear all feedback-related state
        if (isApproved) {
          state.byStepInstance[stepInstanceId].byOutputId[
            outputId
          ].isFeedbackPanelOpen = false;
          state.byStepInstance[stepInstanceId].byOutputId[
            outputId
          ].pendingFeedback = null;
          state.byStepInstance[stepInstanceId].byOutputId[
            outputId
          ].hasFeedback = false;
          state.byStepInstance[stepInstanceId].byOutputId[
            outputId
          ].submittedFeedback = false;
        }
      }
    },

    setOutputHasFeedback: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        hasFeedback: boolean;
      }>,
    ) => {
      const { stepInstanceId, outputId, hasFeedback } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[outputId].hasFeedback =
          hasFeedback;
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].submittedFeedback = hasFeedback;
      }
    },

    setPendingFeedback: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        feedbackText: string | null;
      }>,
    ) => {
      const { stepInstanceId, outputId, feedbackText } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].pendingFeedback = feedbackText;
      }
    },

    setSelectedOptions: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        options: string[] | null;
      }>,
    ) => {
      const { stepInstanceId, outputId, options } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].selectedOptions = options;
      }
    },

    setSelectedCount: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        count: number;
      }>,
    ) => {
      const { stepInstanceId, outputId, count } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        const outputState =
          state.byStepInstance[stepInstanceId].byOutputId[outputId];

        outputState.selectedCount = count;

        if (count > 0 && outputState.hasFeedback) {
          outputState.hasFeedback = false;
          outputState.submittedFeedback = false;
          outputState.pendingFeedback = null;
        }
      }
    },

    addSubmittedFeedback: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
      }>,
    ) => {
      const { stepInstanceId, outputId } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].submittedFeedback = true;
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].selectedCount = 0;
      }
    },

    setFeedbackPanelOpen: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputId: string;
        isOpen: boolean;
      }>,
    ) => {
      const { stepInstanceId, outputId, isOpen } = action.payload;

      if (state.byStepInstance[stepInstanceId]?.byOutputId[outputId]) {
        state.byStepInstance[stepInstanceId].byOutputId[
          outputId
        ].isFeedbackPanelOpen = isOpen;

        // When closing the panel, also clear pending feedback
        if (!isOpen) {
          state.byStepInstance[stepInstanceId].byOutputId[
            outputId
          ].pendingFeedback = null;
        }
      }
    },

    syncOutputFromServer: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        output: WorkflowStepInstanceOutput;
      }>,
    ) => {
      const { stepInstanceId, output } = action.payload;

      if (!state.byStepInstance[stepInstanceId]) {
        state.byStepInstance[stepInstanceId] = { byOutputId: {} };
      }

      if (state.byStepInstance[stepInstanceId].byOutputId[output.id]) {
        const current =
          state.byStepInstance[stepInstanceId].byOutputId[output.id];
        current.isApproved = output.isApproved;
        current.hasFeedback = output.hasFeedback;
        // When server hasFeedback is true, mark as submitted and clear pending
        if (output.hasFeedback) {
          current.submittedFeedback = true;
          current.pendingFeedback = null;
        }
      }
    },

    syncOutputsFromWorkflow: (
      state,
      action: PayloadAction<{
        stepInstanceId: string;
        outputs: WorkflowStepInstanceOutput[];
      }>,
    ) => {
      const { stepInstanceId, outputs } = action.payload;

      if (!state.byStepInstance[stepInstanceId]) {
        state.byStepInstance[stepInstanceId] = { byOutputId: {} };
      }

      outputs.forEach((output) => {
        if (state.byStepInstance[stepInstanceId].byOutputId[output.id]) {
          const current =
            state.byStepInstance[stepInstanceId].byOutputId[output.id];
          current.isApproved = output.isApproved;
          current.hasFeedback = output.hasFeedback;
          if (output.hasFeedback) {
            current.submittedFeedback = true;
            current.pendingFeedback = null;
          }
        }
      });
    },

    resetStepOutputFeedback: (
      state,
      action: PayloadAction<string>, // stepInstanceId
    ) => {
      const stepInstanceId = action.payload;
      const stepState = state.byStepInstance[stepInstanceId];

      if (stepState) {
        Object.values(stepState.byOutputId).forEach((outputState) => {
          outputState.hasFeedback = false;
          outputState.submittedFeedback = false;
          outputState.pendingFeedback = null;
          outputState.isFeedbackPanelOpen = false;
        });
      }
    },

    clearOutputStateForStep: (
      state,
      action: PayloadAction<string>, // stepInstanceId
    ) => {
      delete state.byStepInstance[action.payload];
    },

    // Clear all output state
    clearAllOutputState: (state) => {
      state.byStepInstance = {};
    },
  },
});

export const {
  initialiseStepOutputState,
  setOutputApproval,
  setOutputHasFeedback,
  setPendingFeedback,
  setSelectedOptions,
  setSelectedCount,
  addSubmittedFeedback,
  setFeedbackPanelOpen,
  syncOutputFromServer,
  syncOutputsFromWorkflow,
  resetStepOutputFeedback,
  clearOutputStateForStep,
  clearAllOutputState,
} = outputStateSlice.actions;

export const outputStateReducer = outputStateSlice.reducer;

// Selectors
export const selectOutputStateByStep = (
  state: RootState,
  stepInstanceId: string,
) => state.outputState.byStepInstance[stepInstanceId]?.byOutputId ?? {};

export const selectSingleOutputState = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
) => state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId];

export const selectOutputApprovals = (
  state: RootState,
  stepInstanceId: string,
): Record<string, boolean> => {
  const byOutputId =
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId ?? {};
  const result: Record<string, boolean> = {};
  Object.entries(byOutputId).forEach(([outputId, state]) => {
    result[outputId] = state.isApproved;
  });
  return result;
};

export const selectOutputHasFeedback = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
): boolean => {
  return (
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId]
      ?.hasFeedback ?? false
  );
};

export const selectPendingFeedback = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
): string | null => {
  return (
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId]
      ?.pendingFeedback ?? null
  );
};

export const selectSelectedOptions = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
): string[] | null => {
  return (
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId]
      ?.selectedOptions ?? null
  );
};

export const selectIsFeedbackPanelOpen = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
): boolean => {
  return (
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId]
      ?.isFeedbackPanelOpen ?? false
  );
};

// Should this just be a utility function?
export const selectIsOutputValid = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
): boolean => {
  const outputState =
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId];
  if (!outputState) {
    log("[outputStateSlice]", "selectIsOutputValid: No output state found", {
      stepInstanceId,
      outputId,
    });
    return false;
  }

  // If feedback panel is open and no feedback has been submitted, output is invalid
  if (outputState.isFeedbackPanelOpen && !outputState.submittedFeedback) {
    return false;
  }

  return (
    outputState.isApproved ||
    outputState.hasFeedback ||
    outputState.submittedFeedback
  );
};

// Compute if all outputs in a step are valid
export const selectCanSubmitStep = (
  state: RootState,
  stepInstanceId: string,
  outputIds: string[],
): boolean => {
  if (outputIds.length === 0) {
    log("[outputStateSlice]", "selectCanSubmitStep: No outputs provided", {
      stepInstanceId,
    });
    return false;
  }

  return outputIds.every((outputId) =>
    selectIsOutputValid(state, stepInstanceId, outputId),
  );
};

export const selectSelectOutputIsValid = (
  state: RootState,
  stepInstanceId: string,
  outputId: string,
  output: WorkflowStepInstanceOutput,
): boolean => {
  const outputState =
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId[outputId];

  if (!outputState) {
    return false;
  }

  const hasFeedback = outputState.submittedFeedback || output.hasFeedback;
  if (hasFeedback && outputState.selectedCount === 0) {
    return true;
  }

  const { min, max } = getOptionConstraints(output);
  const selectedCount = outputState.selectedCount;

  if (
    output.workflowStepOutputDefinition.properties.find(
      (p) => p.key === "MultiSelect",
    )?.value === false
  ) {
    const isValid = selectedCount === 1;
    return isValid;
  } else {
    const meetsMin = min === undefined || selectedCount >= min;
    const meetsMax = max === undefined || selectedCount <= max;
    const isValid = meetsMin && meetsMax;

    return isValid;
  }
};

export const selectCanSubmitSelectStep = (
  state: RootState,
  stepInstanceId: string,
  outputs: WorkflowStepInstanceOutput[],
): boolean => {
  if (outputs.length === 0) return false;

  return outputs.every((output) =>
    selectSelectOutputIsValid(state, stepInstanceId, output.id, output),
  );
};

export const selectSelectOutputValidationStatus = (
  state: RootState,
  stepInstanceId: string,
  outputs: WorkflowStepInstanceOutput[],
): Record<string, boolean> => {
  const result: Record<string, boolean> = {};
  outputs.forEach((output) => {
    result[output.id] = selectSelectOutputIsValid(
      state,
      stepInstanceId,
      output.id,
      output,
    );
  });
  return result;
};

export const selectOutputValidationStatus = (
  state: RootState,
  stepInstanceId: string,
  outputIds: string[],
): Record<string, boolean> => {
  const result: Record<string, boolean> = {};
  outputIds.forEach((outputId) => {
    result[outputId] = selectIsOutputValid(state, stepInstanceId, outputId);
  });
  return result;
};

export const selectAnyOutputHasFeedback = (
  state: RootState,
  stepInstanceId: string,
): boolean => {
  const byOutputId =
    state.outputState.byStepInstance[stepInstanceId]?.byOutputId ?? {};
  return Object.values(byOutputId).some(
    (outputState) => outputState.hasFeedback || outputState.submittedFeedback,
  );
};
